import React, { useState } from 'react';
import './EnergyAI.scss';
import { Helmet } from 'react-helmet';
import { useInView } from '../hooks/useInView';

import logoEnergyAi from '../Assets/Images/energyai/logo-energyai.svg';
import heroBg from '../Assets/Images/energyai/eai-hero-bg.svg';

import whyBg from '../Assets/Images/energyai/eai-why-bg.png';
import iconComplexity from '../Assets/Images/energyai/eai-icon-complexity.png';
import iconEconomy from '../Assets/Images/energyai/eai-icon-economy.png';
import iconPlanning from '../Assets/Images/energyai/eai-icon-planning.png';

import colData from '../Assets/Images/energyai/eai-col-data.png';
import colProcessing from '../Assets/Images/energyai/eai-col-processing.png';
import colOutput from '../Assets/Images/energyai/eai-col-output.png';
import stepIngestion from '../Assets/Images/energyai/eai-step-ingestion.png';
import stepTransform from '../Assets/Images/energyai/eai-step-transform.png';
import stepRules from '../Assets/Images/energyai/eai-step-rules.png';
import stepModel from '../Assets/Images/energyai/eai-step-model.png';
import stepApi from '../Assets/Images/energyai/eai-step-api.png';
import stepAnalysis from '../Assets/Images/energyai/eai-step-analysis.png';

import outFaster from '../Assets/Images/energyai/eai-out-faster.png';
import outError from '../Assets/Images/energyai/eai-out-error.png';
import outRealtime from '../Assets/Images/energyai/eai-out-realtime.png';
import outCore from '../Assets/Images/energyai/eai-out-core.png';
import outcomesBg from '../Assets/Images/energyai/eai-outcomes-bg.png';
import solutionMobBg from '../Assets/Images/energyai/eai-solution-mob-bg.png';

const whyCards = [
  {
    icon: iconComplexity,
    title: 'Rising system complexity',
    desc: 'Load fluctuations, cross-border flows, and grid constraints are increasing the need for reliable demand and price intelligence across European markets.',
  },
  {
    icon: iconEconomy,
    title: 'Economic opportunity',
    desc: 'Volatility in energy demand and production creates opportunity for optimization that can act faster. AI-driven forecasting enables more accurate price and load predictions in real time.',
  },
  {
    icon: iconPlanning,
    title: 'Key planning metric',
    desc: 'Forecasting daily electricity consumption and price directly influences production planning and open market trading, while heavily impacting grid balancing.',
  },
];

const dataCategories = [
  'Electricity consumption',
  'Electricity production',
  'Transport capacities',
  'Electricity price',
  'Planned outages',
  'Specific days & calendar',
  'Weather data',
];

const processingSteps = [
  { icon: stepIngestion, title: 'Data Ingestion', desc: 'Automated collection of historical, actual, and forecast data from multiple sources.' },
  { icon: stepTransform, title: 'Transform & Load', desc: 'Data cleaning, transformation, and structuring into the central data store.' },
  { icon: stepRules, title: 'Business Rules', desc: 'Application of domain-specific logic and constraints to the data.' },
  { icon: stepModel, title: 'Model Training', desc: 'ML model training on consolidated data with continuous learning cycle.' },
];

const outcomeCards = [
  {
    icon: outFaster,
    stat: '8X',
    statLabel: 'Faster',
    title: 'forecasting',
    desc: 'From forecasting that took hours of manual work, transformed into automatic AI output in minutes — giving traders and operators speed advantage needed in volatile markets.',
  },
  {
    icon: outError,
    stat: '<2%',
    statLabel: 'Forecast error',
    title: '(MAE)',
    desc: 'The platform consolidates data from multiple sources into a single unified model that continuously improves — achieving industry-leading accuracy in load and price prediction.',
  },
  {
    icon: outRealtime,
    stat: '',
    statLabel: '',
    title: <>Real-time<br />intelligence</>,
    desc: 'Rising grid complexity driven by cross-border flows, load fluctuations, and renewable energy integration demands reliable, real-time intelligence — Energy AI is purpose-built for this.',
  },
  {
    icon: outCore,
    stat: '',
    statLabel: '',
    title: <>Core operational<br />asset</>,
    desc: 'Forecasting daily electricity consumption and price directly influences production planning, open-market trading, and grid balancing — making prediction a strategic advantage, not a report.',
  },
];

function EnergyAI() {
  const [expandedWhy, setExpandedWhy] = useState<number | null>(null);
  const [expandedOutcome, setExpandedOutcome] = useState<number | null>(null);
  const [expandedProcessing, setExpandedProcessing] = useState<number | null>(null);
  const [expandedOutput, setExpandedOutput] = useState<number | null>(null);
  const { ref: heroRef, visible: heroVisible } = useInView(0.05);
  const { ref: whyRef, visible: whyVisible } = useInView();
  const { ref: solutionRef, visible: solutionVisible } = useInView();
  const { ref: outcomesRef, visible: outcomesVisible } = useInView();
  const { ref: ctaRef, visible: ctaVisible } = useInView();

  return (
    <div className="eai-page">
      <Helmet>
        <title>Artifit - Energy AI</title>
        <meta name="description" content="AI-driven energy demand and price forecasting platform for producers, traders, and distributors." />
      </Helmet>

      {/* ── HERO ── */}
      <section className="eai-hero">
        <img src={heroBg} alt="" className="eai-hero-bg" />
        <div
          className={`eai-hero-content anim-fade-up${heroVisible ? ' is-visible' : ''}`}
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <div className="eai-hero-logo">
            <img className="eai-hero-logo-icon" src={logoEnergyAi} alt="" />
            <span className="eai-hero-logo-text">Energy AI</span>
          </div>
          <p className="eai-hero-label">Data Platform &amp; ML Solutions</p>
          <h1 className="eai-hero-title">
            <span className="eai-orange">Energy</span> demand &amp;<br />
            Price Forecasting
          </h1>
          <p className="eai-hero-desc">
            AI-driven platform that predicts electricity demand and prices with
            industry-leading accuracy — built for producers, traders, and system
            operators who need to act faster than the market.
          </p>
          <a href="mailto:office@artifit.rs" rel="noreferrer" target="_blank" className="eai-btn-primary">Get in touch</a>
        </div>
      </section>

      {/* ── WHY IT MATTERS ── */}
      <section className="eai-why">
        <img src={whyBg} alt="" className="eai-why-bg" />
        <div
          className={`eai-section-inner anim-fade-up${whyVisible ? ' is-visible' : ''}`}
          ref={whyRef as React.RefObject<HTMLDivElement>}
        >
          <h2 className="eai-section-title">
            <span className="eai-orange">Why</span> it matters?
          </h2>
          <p className="eai-why-intro">
            Lead on digital path by turning complexity into clarity:<br />
            Data consolidation and AI application for better decision-making.
          </p>
          <div className="eai-why-grid">
            {whyCards.map((card, i) => (
              <div
                className={`eai-why-card${expandedWhy === i ? ' eai-why-card--open' : ''}`}
                key={i}
                onClick={() => setExpandedWhy(expandedWhy === i ? null : i)}
              >
                <div className="eai-why-card-inner">
                  <div className="eai-why-card-front">
                    <img src={card.icon} alt="" className="eai-why-icon" />
                    <h3>{card.title}</h3>
                    <svg className="eai-why-chevron" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 7l5 5 5-5" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                  <div className="eai-why-card-back">
                    <p className="eai-why-card-desc">{card.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTION OVERVIEW ── */}
      <section className="eai-solution" id="solution">
        <img src={outcomesBg} alt="" className="eai-solution-blob eai-solution-blob--desktop" />
        <img src={solutionMobBg} alt="" className="eai-solution-blob eai-solution-blob--mobile" />
        <div className="eai-section-inner">
          <h2 className="eai-section-title">
            <span className="eai-orange">Solution</span> overview
          </h2>
          <p className="eai-solution-sub">
            SaaS offering for producers, traders and distributors — platform for accurate
            forecasting using all available data from multiple sources.
          </p>

          <div
            className={`eai-sol-columns${solutionVisible ? ' is-visible' : ''}`}
            ref={solutionRef as React.RefObject<HTMLDivElement>}
          >
            {/* DATA CATEGORIES */}
            <div className="eai-sol-col">
              <div className="eai-sol-col-top">
                <img src={colData} alt="" className="eai-sol-col-icon" />
                <div className="eai-sol-col-header">Data Categories</div>
              </div>
              <ul className="eai-sol-list">
                {dataCategories.map((item, i) => (
                  <li key={i}>
                    <span className="eai-sol-bullet" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="eai-sol-arrow-wrap">
              <div className="eai-sol-arrow-circle">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 7h12M8 2l5 5-5 5" stroke="#ef4223" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* PROCESSING */}
            <div className="eai-sol-col">
              <div className="eai-sol-col-top">
                <img src={colProcessing} alt="" className="eai-sol-col-icon" />
                <div className="eai-sol-col-header">Processing</div>
              </div>
              <div className="eai-sol-steps">
                {processingSteps.map((step, i) => (
                  <div
                    className={`eai-sol-step${expandedProcessing === i ? ' eai-sol-step--open' : ''}`}
                    key={i}
                    onClick={() => setExpandedProcessing(expandedProcessing === i ? null : i)}
                  >
                    <img src={step.icon} alt="" className="eai-sol-step-icon" />
                    <div className="eai-sol-step-content">
                      <div className="eai-sol-step-title">{step.title}</div>
                      <div className="eai-sol-step-desc">{step.desc}</div>
                    </div>
                    <svg className="eai-sol-step-chevron" width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M3 6l5 5 5-5" stroke="#ef4223" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                ))}
              </div>
            </div>

            <div className="eai-sol-arrow-wrap">
              <div className="eai-sol-arrow-circle">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 7h12M8 2l5 5-5 5" stroke="#ef4223" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>

            {/* OUTPUT */}
            <div className="eai-sol-col">
              <div className="eai-sol-col-top">
                <img src={colOutput} alt="" className="eai-sol-col-icon" />
                <div className="eai-sol-col-header">Output</div>
              </div>
              <div className="eai-sol-steps">
                <div
                  className={`eai-sol-step${expandedOutput === 0 ? ' eai-sol-step--open' : ''}`}
                  onClick={() => setExpandedOutput(expandedOutput === 0 ? null : 0)}
                >
                  <img src={stepApi} alt="" className="eai-sol-step-icon" />
                  <div className="eai-sol-step-content">
                    <div className="eai-sol-step-title">API Output</div>
                    <div className="eai-sol-step-desc">Forecast results exposed via API for integration with trading and operational systems.</div>
                  </div>
                  <svg className="eai-sol-step-chevron" width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M3 6l5 5 5-5" stroke="#ef4223" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <div
                  className={`eai-sol-step${expandedOutput === 1 ? ' eai-sol-step--open' : ''}`}
                  onClick={() => setExpandedOutput(expandedOutput === 1 ? null : 1)}
                >
                  <img src={stepAnalysis} alt="" className="eai-sol-step-icon" />
                  <div className="eai-sol-step-content">
                    <div className="eai-sol-step-title">Analysis</div>
                    <div className="eai-sol-step-desc">Visualization, interpretation, and self-service analytics for business users.</div>
                  </div>
                  <svg className="eai-sol-step-chevron" width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M3 6l5 5 5-5" stroke="#ef4223" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
              </div>
              <div className="eai-sol-divider" />
              <div className="eai-sol-data-types">
                <span className="eai-sol-types-label eai-orange">Data Types</span>
                <div className="eai-sol-tags">
                  <span className="eai-sol-tag">Historical</span>
                  <span className="eai-sol-tag">Actual</span>
                  <span className="eai-sol-tag">Forecasted</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUTCOMES ── */}
      <section className="eai-outcomes">
        <div
          className={`eai-section-inner anim-fade-up${outcomesVisible ? ' is-visible' : ''}`}
          ref={outcomesRef as React.RefObject<HTMLDivElement>}
        >
          <h2 className="eai-section-title">
            Out<span className="eai-orange">comes</span>
          </h2>
          <div className="eai-outcomes-grid">
            {outcomeCards.map((card, i) => (
              <div
                className={`eai-outcome-card${expandedOutcome === i ? ' eai-outcome-card--open' : ''}`}
                key={i}
                onClick={() => setExpandedOutcome(expandedOutcome === i ? null : i)}
              >
                <div className="eai-outcome-card-top">
                  <img src={card.icon} alt="" className="eai-outcome-icon" />
                  <div className="eai-outcome-card-info">
                    {card.stat ? (
                      <div className="eai-outcome-stat">
                        <span className="eai-orange eai-outcome-stat-num">{card.stat} </span>
                        <span className="eai-outcome-stat-label">{card.statLabel}</span>
                        {card.title && <div className="eai-outcome-stat-sub">{card.title}</div>}
                      </div>
                    ) : (
                      <h3 className="eai-outcome-title">{card.title}</h3>
                    )}
                  </div>
                  <svg className="eai-outcome-chevron" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M4 7l5 5 5-5" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p className="eai-outcome-desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="eai-cta">
        <div
          className={`eai-cta-inner anim-fade-up${ctaVisible ? ' is-visible' : ''}`}
          ref={ctaRef as React.RefObject<HTMLDivElement>}
        >
          <h2>Ready to transform your<br />energy forecasting?</h2>
          <p>Let's talk about how Energy AI can reduce your balancing costs and improve decision-making.</p>
          <a href="mailto:office@artifit.rs" rel="noreferrer" target="_blank">
            <button className="eai-btn-cta">Get in touch</button>
          </a>
        </div>
      </section>

    </div>
  );
}

export default EnergyAI;
